import { CardKind, getCardKind, isExtraDeckCard } from '../types/cards';
import { ActionType, PlayerAction } from '../types/actions';
import {
  CardInstance,
  CardPosition,
  EnginePhase,
  HAND_SIZE_LIMIT,
  MatchState,
  Seat,
  otherSeat,
} from '../types/state';
import {
  CardLibrary,
  getCardData,
  getLevel,
  getModifiedAtk,
  getModifiedDef,
  getMonsters,
  getSpellTraps,
  soulExchangeTributeUids,
} from './helpers';
import {
  BURN_DAMAGE,
  combatOutlookScore,
  forecastCombat,
  knownCardId,
  SET_ATTACK_MIN_ATK,
  shouldMonsterBeInAttack,
} from './aiCombat';
import { getEquipConfig, getFieldSpellConfig, FieldSpellConfig } from './effects';
import { aiIsHighDifficulty, plannerDifficulty, type AiDeckDifficulty } from './aiDeck';
import { EXODIA_PIECE_SET } from './aiRecipes';

/** LOB cards the AI should almost always fire when legal. */
export const AI_CARD = {
  DARK_HOLE: '53129443',
  RAIGEKI: '12580477',
  POT_OF_GREED: '55144522',
  FISSURE: '66788016',
  MONSTER_REBORN: '83764718',
  CHANGE_OF_HEART: '4031928',
  SWORDS: '72302403',
  POLYMERIZATION: '24094653',
  STOP_DEFENSE: '63102017',
  CARD_DESTRUCTION: '72892473',
  TRAP_HOLE: '4206964',
  LAST_WILL: '85602018',
} as const;

/**
 * Spells worth holding. Card Destruction should not throw these away.
 * Fissure, Swords and Polymerization are not on this list.
 */
const SAVED_SPELLS = new Set<string>([
  AI_CARD.POT_OF_GREED,
  AI_CARD.RAIGEKI,
  AI_CARD.DARK_HOLE,
  AI_CARD.MONSTER_REBORN,
  AI_CARD.CHANGE_OF_HEART,
]);

/** La Jinn line. A 1700 is the bait; 1800 is what Trap Hole should answer. */
const BAIT_ATK = 1800;
const BAIT_FLOOR = 1000;

const POWER_SPELLS = new Set<string>([
  AI_CARD.DARK_HOLE,
  AI_CARD.RAIGEKI,
  AI_CARD.POT_OF_GREED,
  AI_CARD.FISSURE,
  AI_CARD.MONSTER_REBORN,
  AI_CARD.CHANGE_OF_HEART,
  AI_CARD.SWORDS,
  AI_CARD.POLYMERIZATION,
]);

const WIN = 1_000_000;
const LP_W = 1.15;
const OUR_ATK_W = 0.45;
const OUR_DEF_W = 0.22;
const OPP_ATK_W = 0.55;
const OPP_DEF_W = 0.28;
const HAND_W = 70;
const SET_BACKROW_W = 85;
const OUR_SET_MONSTER_W = 0.28;
const OPP_SET_MONSTER_W = 55;
const LOCK_W = 220;

/**
 * Board score from public information. Face-down monsters are a flat unknown;
 * their ATK and DEF are never read.
 */
export function evaluateState(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): number {
  if (state.status === 'finished') {
    if (state.winner === seat) return WIN;
    if (state.winner === otherSeat(seat)) return -WIN;
    return 0;
  }

  const z = state.zones[seat];
  const opp = otherSeat(seat);
  const oz = state.zones[opp];
  let score = (z.lifePoints - oz.lifePoints) * LP_W;

  score += scoreOurMonsters(z, library, state.reverseStatMods);
  const oppPublic = scoreOppMonsters(oz, library, state.reverseStatMods);
  score += oppPublic.score;
  score += z.hand.length * HAND_W;
  score += uniqueExodiaPieces(z.hand) * 1400;
  score += z.deck.length * 4;
  score += setBackrowCount(z) * SET_BACKROW_W;
  if (oz.attackLockTurns > 0) score += oz.attackLockTurns * LOCK_W;

  if (oppPublic.atk > 0 && getMonsters(z).length === 0) score -= 450;
  if (oppPublic.faceUpCount === 0 && getMonsters(z).some(isFaceUpAttacker)) {
    score += 80;
  }

  const ourSwing = forecastCombat(state, library, seat);
  const theirSwing = forecastCombat(state, library, opp);
  score += ourSwing.attackerDamage * 0.4;
  score += ourSwing.defenderAtkDestroyed * 0.5;
  score -= ourSwing.attackerAtkLost * 0.45;
  score -= theirSwing.attackerDamage * 1.0;
  score -= theirSwing.defenderAtkDestroyed * 0.5;
  const givenBack = borrowedAttack(z, library, state.reverseStatMods);
  score -= givenBack * OPP_ATK_W;
  score -= givenBack * 0.4;
  if (ourSwing.lethal) score += 50_000;
  if (theirSwing.lethal) score -= 40_000;
  return score;
}

/**
 * Face-up ATK we only hold until the End Phase (Change of Heart). The body
 * goes back next turn, and the owner can change its position then, so
 * Defense does not hide it. Counting only Attack Position made the CPU
 * flip a stolen Summoned Skull to Defense instead of attacking with it.
 */
function borrowedAttack(
  z: MatchState['zones'][Seat],
  library: CardLibrary,
  reverseMods: boolean,
): number {
  let atk = 0;
  for (const m of getMonsters(z)) {
    if (m.controlReturnTo === undefined) continue;
    if (!m.faceUp) continue;
    atk += getModifiedAtk(m, library, reverseMods);
  }
  return atk;
}

function scoreOurMonsters(
  z: MatchState['zones'][Seat],
  library: CardLibrary,
  reverseMods: boolean,
): number {
  let score = 0;
  for (const m of getMonsters(z)) {
    if (m.controlReturnTo !== undefined) continue;
    if (m.faceUp) {
      if (m.position === CardPosition.ATTACK) {
        score += getModifiedAtk(m, library, reverseMods) * OUR_ATK_W;
      } else {
        score += getModifiedDef(m, library, reverseMods) * OUR_DEF_W;
      }
    } else {
      const wall = Math.max(
        getModifiedAtk(m, library, reverseMods),
        getModifiedDef(m, library, reverseMods),
      );
      score += wall * OUR_SET_MONSTER_W;
    }
  }
  return score;
}

function scoreOppMonsters(
  oz: MatchState['zones'][Seat],
  library: CardLibrary,
  reverseMods: boolean,
): { atk: number; faceUpCount: number; score: number } {
  let atk = 0;
  let faceUpCount = 0;
  let score = 0;
  for (const m of getMonsters(oz)) {
    if (!m.faceUp) {
      score -= OPP_SET_MONSTER_W;
      continue;
    }
    faceUpCount += 1;
    if (m.position === CardPosition.ATTACK) {
      const value = getModifiedAtk(m, library, reverseMods);
      atk += value;
      score -= value * OPP_ATK_W;
    } else {
      score -= getModifiedDef(m, library, reverseMods) * OPP_DEF_W;
    }
  }
  return { atk, faceUpCount, score };
}

function setBackrowCount(z: MatchState['zones'][Seat]): number {
  let n = getSpellTraps(z).filter((c) => !c.faceUp).length;
  if (z.fieldSpell && !z.fieldSpell.faceUp) n += 1;
  return n;
}

function isFaceUpAttacker(m: CardInstance): boolean {
  return m.faceUp && m.position === CardPosition.ATTACK;
}

function uniqueExodiaPieces(hand: CardInstance[]): number {
  const ids = new Set(
    hand.filter((c) => EXODIA_PIECE_SET.has(c.cardId)).map((c) => c.cardId),
  );
  return ids.size;
}

function cardIdOfUid(
  state: MatchState,
  seat: Seat,
  uid: string,
): string | null {
  const z = state.zones[seat];
  const hit =
    z.hand.find((c) => c.uid === uid) ||
    getMonsters(z).find((c) => c.uid === uid) ||
    getSpellTraps(z).find((c) => c.uid === uid);
  return hit?.cardId ?? null;
}

/**
 * Keep Forbidden One pieces in hand. Tribute / Set / summon / discard of a
 * piece is almost never correct for an Exodia recipe.
 */
function exodiaPiecePenalty(
  state: MatchState,
  seat: Seat,
  action: PlayerAction,
): number {
  const involved: string[] = [];
  if ('cardUid' in action && typeof action.cardUid === 'string') {
    involved.push(action.cardUid);
  }
  if (action.type === ActionType.TRIBUTE_SUMMON) {
    involved.push(...action.tributeUids);
  }
  if (action.type === ActionType.DISCARD) involved.push(...action.cardUids);
  const n = involved.filter((uid) => {
    const id = cardIdOfUid(state, seat, uid);
    return !!id && EXODIA_PIECE_SET.has(id);
  }).length;
  if (n === 0) return 0;
  if (action.type === ActionType.DISCARD) return -8000 * n;
  return -5000 * n;
}

/**
 * Extra points for a specific action on top of the resulting board. Keeps
 * "always-correct" LOB plays (Pot of Greed) ahead of a mediocre summon.
 */
export function actionBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  action: PlayerAction,
): number {
  if (action.type === ActionType.ADVANCE_PHASE) return 0;
  const piecePenalty = exodiaPiecePenalty(state, seat, action);
  if (action.type === ActionType.DISCARD) return piecePenalty;
  if (action.type === ActionType.NORMAL_SUMMON) {
    return (
      summonAtkBias(state, library, seat, action.cardUid) +
      hiddenSummonBias(state, library, seat, action.cardUid) +
      piecePenalty +
      mukaHandPenalty(state, seat, action.cardUid) +
      thirdBodyPenalty(state, seat) +
      waitsForLastWill(state, library, seat, action.cardUid) +
      classicBodyBias(state, library, seat, action.cardUid, false)
    );
  }
  if (action.type === ActionType.SET_MONSTER) {
    return (
      -summonAtkBias(state, library, seat, action.cardUid) +
      piecePenalty +
      flipSetBias(state, library, seat, action.cardUid) +
      mukaHandPenalty(state, seat, action.cardUid) +
      thirdBodyPenalty(state, seat)
    );
  }
  if (action.type === ActionType.TRIBUTE_SUMMON) {
    const atkBias = summonAtkBias(state, library, seat, action.cardUid);
    const hidden = action.faceDown
      ? 0
      : hiddenSummonBias(state, library, seat, action.cardUid);
    const will = action.faceDown
      ? 0
      : waitsForLastWill(state, library, seat, action.cardUid);
    return (
      (action.faceDown ? -atkBias : atkBias) +
      hidden +
      piecePenalty +
      overkillTributePenalty(state, library, seat, action) +
      will +
      classicBodyBias(state, library, seat, action.cardUid, action.faceDown)
    );
  }
  if (action.type === ActionType.FLIP_SUMMON) {
    return positionChangeBias(state, library, seat, action.cardUid, true);
  }
  if (action.type === ActionType.CHANGE_POSITION) {
    const monster = getMonsters(state.zones[seat]).find((m) => m.uid === action.cardUid);
    const nowAtk = !!monster && monster.faceUp &&
      monster.position === CardPosition.ATTACK;
    return positionChangeBias(state, library, seat, action.cardUid, !nowAtk);
  }
  if (action.type === ActionType.SET_SPELL_TRAP) {
    const id = actionCardId(state, seat, action);
    if (id === AI_CARD.SWORDS && swordsLockAlreadyUp(state, seat)) {
      return -2000;
    }
    if (id && POWER_SPELLS.has(id)) return -500;
    return setBias(state, library, seat, id);
  }

  const cardId = actionCardId(state, seat, action);
  if (!cardId) return 0;

  const z = state.zones[seat];
  const opp = otherSeat(seat);
  const ourPublic = publicAtkSum(z.monsters, library, state.reverseStatMods);
  const oppPublic = publicAtkSum(state.zones[opp].monsters, library, state.reverseStatMods);
  const ourSwing = forecastCombat(state, library, seat);
  const theirSwing = forecastCombat(state, library, opp);
  const oppLp = state.zones[opp].lifePoints;
  const burn = BURN_DAMAGE[cardId] ?? 0;

  switch (cardId) {
    case AI_CARD.POT_OF_GREED:
      return 900;
    case AI_CARD.RAIGEKI:
      if (plannerDifficulty(state, seat) === 'easy') return 120 + oppPublic * 0.35;
      return removalHoldBias(state, seat, oppPublic, ourPublic, ourSwing, theirSwing);
    case AI_CARD.DARK_HOLE:
      if (plannerDifficulty(state, seat) === 'easy') {
        if (getMonsters(state.zones[opp]).length === 0) return -600;
        if (ourSwing.lethal && !theirSwing.lethal) return -800;
        if (combatOutlookScore(state, library, seat) < 0) return 280;
        if (theirSwing.lethal) return 400;
        if (oppPublic > ourPublic) return 80;
        return -600;
      }
      return removalHoldBias(state, seat, oppPublic, ourPublic, ourSwing, theirSwing);
    case AI_CARD.FISSURE:
      return fissureBias(state, seat);
    case AI_CARD.MONSTER_REBORN:
      return rebornTimingBias(state, library, seat);
    case AI_CARD.CHANGE_OF_HEART:
      return changeOfHeartBias(state, library, seat, action);
    case AI_CARD.LAST_WILL:
      return lastWillBias(state, library, seat);
    case AI_CARD.POLYMERIZATION:
      return 80 - fusionHandPenalty(state, library, seat, action);
    case AI_CARD.SWORDS:
      // A second copy does not stack (Math.max lock). Do not waste it.
      if (swordsLockAlreadyUp(state, seat)) return -2000;
      if (ourSwing.lethal) return -80;
      if (theirSwing.lethal) return 320;
      if (theirSwing.attackerDamage >= 800 || theirSwing.defenderAtkDestroyed > 0) {
        return 280;
      }
      if (theirSwing.attackerDamage > 0) return 120;
      return 40;
    case AI_CARD.STOP_DEFENSE:
      return stopDefenseBias(state, library, seat, action);
    case AI_CARD.CARD_DESTRUCTION:
      return cardDestructionBias(state, seat);
    case '19613556':
      return heavyStormBias(state, library, seat);
    default: {
      if (cardId === SOUL_EXCHANGE) return soulExchangeBias(state, library, seat, action);
      if (cardId === '19159413') return deSpellBias(state, seat, action);
      const heal = HEAL_AMOUNT[cardId];
      if (heal) return healBias(state, library, seat, heal);
      if (burn > 0) {
        if (cardId === '46918794' && z.lifePoints <= 500) return -2000;
        if (burn + ourSwing.attackerDamage >= oppLp) return 800;
        if (theirSwing.lethal) return -40;
        return Math.min(120, burn * 0.15);
      }
      const equip = getEquipConfig(Number(cardId));
      if (equip) return equipBias(state, library, seat, action, equip.atkBoost);
      const field = getFieldSpellConfig(Number(cardId));
      if (field) return fieldSpellBias(state, library, seat, field);
      if (cardId === '25833572' && action.type === ActionType.SPECIAL_SUMMON_PROC) {
        return gateGuardianBias(state, seat);
      }
      return ignitionBias(state, seat, cardId);
    }
  }
}

/**
 * Setting a Trap is the only way to play it. A Spell gains nothing face-down —
 * it still cannot be chained at Spell Speed 2 — and "Set, then activate the Set
 * card" ends on the same board as activating from the hand, so a positive bias
 * there would always win that path. Only a hand over the limit prefers a Set
 * over the End Phase discard.
 */
function setBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  cardId: string | null,
): number {
  const isSpell =
    !!cardId && getCardKind(getCardData(library, cardId)) === CardKind.SPELL;
  if (isSpell && state.zones[seat].hand.length <= HAND_SIZE_LIMIT) return -150;
  return state.phase === EnginePhase.MAIN_2 ? 70 : 25;
}

function summonAtkBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  cardUid: string,
): number {
  const card = state.zones[seat].hand.find((c) => c.uid === cardUid);
  if (!card) return 0;
  const fake: CardInstance = {
    ...card,
    faceUp: true,
    position: CardPosition.ATTACK,
    attackedThisTurn: false,
  };
  const wantNow = shouldMonsterBeInAttack(state, library, seat, fake, false);
  const wantBoosted = shouldMonsterBeInAttack(state, library, seat, fake, true);
  let bias = 0;
  if (!wantBoosted) bias -= 280;
  if (wantBoosted && !wantNow) bias += 200;
  return bias;
}

/** Fear of one untested Set when summoning ATK >= 1000. A body is worth ~0.45 per ATK. */
function trapHoleFearPerSet(level: AiDeckDifficulty): number {
  switch (level) {
    case 'test':
    case 'easy':
      return 50;
    case 'normal':
      return 220;
    case 'hard':
      return 500;
    case 'expert':
      return 700;
    case 'master':
      return 900;
  }
}

/**
 * Extra pull toward a 1000–1799 Normal Summon, and away from an 1800+, while
 * a Set might still be Trap Hole. Easy does not split the line.
 */
function eraBaitSplit(level: AiDeckDifficulty): number {
  switch (level) {
    case 'test':
    case 'easy':
      return 0;
    case 'normal':
      return 160;
    case 'hard':
      return 450;
    case 'expert':
      return 550;
    case 'master':
      return 650;
  }
}

/** Hard+ prefers a sub-1000 summon as bait while a Set might still be Trap Hole. */
const BAIT_SUMMON_BONUS = 400;

/**
 * Sets old enough to activate that have not already passed a Trap Hole window.
 * Ids are never read, so a Set spell stays scary until that window happens.
 */
function scaryTrapHoleSets(state: MatchState, seat: Seat): number {
  const passed = new Set(state.trapHolePassedUids ?? []);
  return getSpellTraps(state.zones[otherSeat(seat)]).filter((c) => {
    if (c.faceUp || c.setOnTurn >= state.absoluteTurnCount || passed.has(c.uid)) {
      return false;
    }
    const known = knownCardId(state, seat, c.uid);
    return !known || known === AI_CARD.TRAP_HOLE;
  }).length;
}

/**
 * Each Trap Hole already in the opponent's Graveyard makes a Set less likely
 * to be another copy. Three spent copies clear the fear.
 */
function trapHoleGraveScale(state: MatchState, seat: Seat): number {
  const copies = state.zones[otherSeat(seat)].graveyard.filter(
    (c) => c.cardId === AI_CARD.TRAP_HOLE,
  ).length;
  return Math.max(0, 1 - copies / 3);
}

/** Level 4 or lower main-deck monsters: the Normal Summon line. */
function normalSummonAtks(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): number[] {
  const atks: number[] = [];
  for (const card of state.zones[seat].hand) {
    const data = getCardData(library, card.cardId);
    if (getCardKind(data) !== CardKind.MONSTER || isExtraDeckCard(data)) continue;
    if (getLevel(card, library) > 4) continue;
    atks.push(getModifiedAtk(card, library, state.reverseStatMods));
  }
  return atks;
}

/**
 * Face-up summon tax for untested backrow, or a bait bonus for a small monster.
 * Kept out of summonAtkBias so Setting the same card does not earn the tax back.
 * When the hand has both a 1700 and an 1800, the 1700 is summoned first.
 */
function hiddenSummonBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  cardUid: string,
): number {
  const scary = scaryTrapHoleSets(state, seat);
  if (scary === 0) return 0;
  const card = state.zones[seat].hand.find((c) => c.uid === cardUid);
  if (!card) return 0;
  const atk = getModifiedAtk(card, library, state.reverseStatMods);
  const level = plannerDifficulty(state, seat);
  const scale = trapHoleGraveScale(state, seat);
  let bias = 0;
  if (atk >= BAIT_FLOOR) bias = -trapHoleFearPerSet(level) * scary * scale;
  else if (aiIsHighDifficulty(level)) bias = BAIT_SUMMON_BONUS;

  if (scale <= 0 || getLevel(card, library) > 4) return bias;
  const atks = normalSummonAtks(state, library, seat);
  const hasBig = atks.some((value) => value >= BAIT_ATK);
  const hasBait = atks.some((value) => value >= BAIT_FLOOR && value < BAIT_ATK);
  if (!hasBig || !hasBait) return bias;
  const split = eraBaitSplit(level) * scale;
  if (atk >= BAIT_ATK) bias -= split;
  else if (atk >= BAIT_FLOOR) bias += split;
  return bias;
}

/**
 * A third monster is a Dark Hole or Raigeki. Each is one copy, so the hold
 * ends once both are in the opponent's Graveyard. A lethal line still wins:
 * the board score for game is far larger than this penalty.
 */
function thirdBodyPenalty(state: MatchState, seat: Seat): number {
  const level = plannerDifficulty(state, seat);
  if (level === 'easy' || level === 'test') return 0;
  if (getMonsters(state.zones[seat]).length < 2) return 0;
  const gy = state.zones[otherSeat(seat)].graveyard;
  const spent = (id: string) => gy.some((card) => card.cardId === id);
  if (spent(AI_CARD.RAIGEKI) && spent(AI_CARD.DARK_HOLE)) return 0;
  return aiIsHighDifficulty(level) ? -3000 : -2200;
}

/**
 * Last Will is the insurance for a 1000+ summon while a Set might be Trap Hole.
 * The summon itself waits, so the spell is the action the search takes first.
 */
function lastWillInsurance(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): boolean {
  const level = plannerDifficulty(state, seat);
  if (level === 'easy' || level === 'test') return false;
  const zone = state.zones[seat];
  if (zone.lastWillCharges > 0) return false;
  if (zone.hasNormalSummoned && zone.extraNormalSummons <= 0) return false;
  if (scaryTrapHoleSets(state, seat) <= 0 || trapHoleGraveScale(state, seat) <= 0) {
    return false;
  }
  let will = false;
  let premium = false;
  for (const card of zone.hand) {
    if (card.cardId === AI_CARD.LAST_WILL) {
      will = true;
      continue;
    }
    const data = getCardData(library, card.cardId);
    if (getCardKind(data) !== CardKind.MONSTER || isExtraDeckCard(data)) continue;
    if ((data.atk ?? 0) >= BAIT_FLOOR) premium = true;
  }
  return will && premium;
}

function lastWillBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): number {
  if (!lastWillInsurance(state, library, seat)) return 0;
  return aiIsHighDifficulty(plannerDifficulty(state, seat)) ? 700 : 500;
}

/** Hold the Trap Hole target until Last Will has been activated this turn. */
function waitsForLastWill(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  cardUid: string,
): number {
  if (!lastWillInsurance(state, library, seat)) return 0;
  const card = state.zones[seat].hand.find((c) => c.uid === cardUid);
  if (!card) return 0;
  const atk = getCardData(library, card.cardId).atk ?? 0;
  if (atk < BAIT_FLOOR) return 0;
  return aiIsHighDifficulty(plannerDifficulty(state, seat)) ? -3200 : -2500;
}

/** Muka Muka is 600 plus 300 per card still in hand. Below four, it is a 1500. */
function mukaHandPenalty(
  state: MatchState,
  seat: Seat,
  cardUid: string,
): number {
  const card = state.zones[seat].hand.find((c) => c.uid === cardUid);
  if (!card || Number(card.cardId) !== 46657337) return 0;
  const left = state.zones[seat].hand.length - 1;
  return left >= 4 ? 0 : -700;
}

function ignitionBias(
  state: MatchState,
  seat: Seat,
  cardId: string,
): number {
  const id = Number(cardId);
  if (id === 90219263) return 280;
  if (id === 79759861) return 200;
  if (id === 41142615) return 160;
  if (id === 56830749) return 180;
  if (id === 25880422) return 140;
  if (id === 31786629) {
    const zones = state.zones[seat];
    const jammer = [...zones.hand, ...getSpellTraps(zones)].some(
      (card) => Number(card.cardId) === 77414722,
    );
    const muka = [...zones.hand, ...getMonsters(zones)].some(
      (card) => Number(card.cardId) === 46657337,
    );
    return jammer || muka ? 400 : 180;
  }
  if (id === 11384280) return 350;
  if (id === 95727991) return 400;
  if (id === 71625222) return 2500;
  if (id === 81480460) return 500;
  return 0;
}

function positionChangeBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  cardUid: string,
  goingToAtk: boolean,
): number {
  const monster = getMonsters(state.zones[seat]).find((m) => m.uid === cardUid);
  if (!monster) return 0;
  const id = Number(monster.cardId);
  const oppHasMonster = getMonsters(state.zones[otherSeat(seat)]).length > 0;
  if (id === 13215230 && !goingToAtk && oppHasMonster) return 500;
  if (id === 93889755 && goingToAtk && oppHasMonster) return 450;
  if ((id === 94773007 || id === 21417692) && !goingToAtk) {
    const level = plannerDifficulty(state, seat);
    if (level !== 'easy' && level !== 'test') return -4000;
  }
  if (id === 31560081 || id === 28933734) {
    if (flipTargetUid(state, library, seat, monster.cardId)) return 450;
  }
  // Man-Eater Bug and Hane-Hane must hit a monster. An empty opposing field
  // makes that monster one of ours. Hane-Hane can still bounce our Magician
  // of Faith or Mask of Darkness when that replay is the target.
  if ((id === 54652250 || id === 7089711) && goingToAtk && !oppHasMonster) {
    if (!flipTargetUid(state, library, seat, monster.cardId)) return -2500;
  }
  // A set Sangan (1000 ATK) flipped into two 1500+ attackers only takes
  // battle damage. Left face-down, the crash still searches and deals none.
  // Judge the body as it is, not with an Equip that is still in the hand.
  if (goingToAtk && losesTheCrash(state, library, seat, monster)) return -900;
  const wantAtk = shouldMonsterBeInAttack(state, library, seat, monster, false);
  // Borrowed beaters (Change of Heart) must swing this turn. Main 1 Defense
  // wastes the attack; the owner can still switch when the body returns.
  if (
    monster.controlReturnTo !== undefined &&
    state.phase === EnginePhase.MAIN_1 &&
    !goingToAtk &&
    wantAtk
  ) {
    return -900;
  }
  return goingToAtk === wantAtk ? 200 : -160;
}

/**
 * Flipping this body only eats battle damage: something runs it over, and it
 * cannot win a crash, trade 1-for-1, or walk into a Set.
 */
function losesTheCrash(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  monster: CardInstance,
): boolean {
  const ourAtk = getModifiedAtk(monster, library, state.reverseStatMods);
  const oppMonsters = getMonsters(state.zones[otherSeat(seat)]);
  const oppFace = oppMonsters.filter((m) => m.faceUp);
  const canRemove = oppFace.some((m) => {
    if (m.position === CardPosition.ATTACK) {
      const theirAtk = getModifiedAtk(m, library, state.reverseStatMods);
      return ourAtk >= theirAtk;
    }
    return ourAtk > getModifiedDef(m, library, state.reverseStatMods);
  });
  if (canRemove) return false;
  const hasSet = oppMonsters.some((m) => !m.faceUp);
  if (hasSet && ourAtk >= SET_ATTACK_MIN_ATK) return false;
  return oppFace.some(
    (m) =>
      m.position === CardPosition.ATTACK &&
      getModifiedAtk(m, library, state.reverseStatMods) > ourAtk,
  );
}

/** True while our Swords is already locking the opponent (or just resolved). */
function swordsLockAlreadyUp(state: MatchState, seat: Seat): boolean {
  const opp = otherSeat(seat);
  if (state.zones[opp].attackLockTurns > 0) return true;
  return getSpellTraps(state.zones[seat]).some(
    (c) => c.faceUp && c.cardId === AI_CARD.SWORDS,
  );
}

function actionCardId(
  state: MatchState,
  seat: Seat,
  action: PlayerAction,
): string | null {
  const uid =
    'cardUid' in action && typeof action.cardUid === 'string'
      ? action.cardUid
      : null;
  if (!uid) return null;
  const z = state.zones[seat];
  const fromHand = z.hand.find((c) => c.uid === uid);
  if (fromHand) return fromHand.cardId;
  const fromSt = getSpellTraps(z).find((c) => c.uid === uid);
  if (fromSt) return fromSt.cardId;
  const fromMonster = getMonsters(z).find((c) => c.uid === uid);
  if (fromMonster) return fromMonster.cardId;
  if (z.fieldSpell?.uid === uid) return z.fieldSpell.cardId;
  return null;
}

function publicAtkSum(
  monsters: (CardInstance | null)[],
  library: CardLibrary,
  reverseMods = false,
): number {
  return monsters.reduce((sum, m) => {
    if (!m?.faceUp) return sum;
    return sum + getModifiedAtk(m, library, reverseMods);
  }, 0);
}

function firstTargetUid(action: PlayerAction): string | null {
  if (!('targetUids' in action) || !action.targetUids?.length) return null;
  return action.targetUids[0] ?? null;
}

/** Fissure always kills the lowest face-up ATK. Skip it when a bigger threat stays. */
function fissureBias(state: MatchState, seat: Seat): number {
  const faceUp = getMonsters(state.zones[otherSeat(seat)]).filter((m) => m.faceUp);
  if (faceUp.length === 0) return -600;
  if (faceUp.length > 1) return -400;
  if (plannerDifficulty(state, seat) !== 'easy') return -200;
  return 140;
}

function changeOfHeartBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  action: PlayerAction,
): number {
  const uid = firstTargetUid(action);
  if (!uid) return 40;
  const target = getMonsters(state.zones[otherSeat(seat)]).find((m) => m.uid === uid);
  if (!target) return 40;
  if (!target.faceUp) {
    if (aiIsHighDifficulty(plannerDifficulty(state, seat))) return -400;
    return 60;
  }
  const atk = getModifiedAtk(target, library, state.reverseStatMods);
  return 60 + atk * 0.2;
}

function stopDefenseBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  action: PlayerAction,
): number {
  const uid = firstTargetUid(action);
  if (!uid) return 0;
  const target = getMonsters(state.zones[otherSeat(seat)]).find((m) => m.uid === uid);
  if (!target) return 0;
  // A Set might be Man-Eater Bug, Hane-Hane or Wall of Illusion. Do not read it.
  if (!target.faceUp) {
    if (getMonsters(state.zones[seat]).length > 0) return -800;
    return 160;
  }
  const theirAtk = getModifiedAtk(target, library, state.reverseStatMods);
  const ourBest = getMonsters(state.zones[seat])
    .filter((m) => m.faceUp && m.position === CardPosition.ATTACK)
    .reduce(
      (max, m) => Math.max(max, getModifiedAtk(m, library, state.reverseStatMods)),
      0,
    );
  if (ourBest > theirAtk) return 160;
  if (ourBest === theirAtk) return 40;
  return -180;
}

/**
 * Battleguards want their partner. Shadow Ghoul and Barrel Dragon do not
 * come out early. A 1600 or 2600 body is worth more than a small nudge on
 * the board, so the penalty has to clear that gain. A lethal board is
 * +50000 and still wins.
 */
function classicBodyBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  cardUid: string,
  faceDown: boolean,
): number {
  const level = plannerDifficulty(state, seat);
  if (level === 'easy' || level === 'test') return 0;
  const card = state.zones[seat].hand.find((c) => c.uid === cardUid);
  if (!card) return 0;
  const id = Number(card.cardId);
  let bias = 0;
  if (!faceDown && (id === 40453765 || id === 20394040)) {
    const partner = id === 40453765 ? 20394040 : 40453765;
    const up = getMonsters(state.zones[seat]).some(
      (monster) => monster.faceUp && Number(monster.cardId) === partner,
    );
    if (up) bias += 600;
  }
  if (id === 30778711) {
    const gy = state.zones[seat].graveyard.filter(
      (grave) => getCardKind(getCardData(library, grave.cardId)) === CardKind.MONSTER,
    ).length;
    if (gy < 5) bias -= 1800;
  }
  if (id === 81480460) bias -= 2800;
  return bias;
}

/** Three 2400s into one 3750 loses Attack. Only a lethal swing is worth it. */
function gateGuardianBias(state: MatchState, seat: Seat): number {
  const level = plannerDifficulty(state, seat);
  if (level === 'easy' || level === 'test') return 0;
  return -2500;
}

/**
 * A 2-tribute body is overkill when a 1-tribute already runs over every
 * face-up monster. Blue-Eyes stays correct when that 1-tribute cannot.
 */
function overkillTributePenalty(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  action: Extract<PlayerAction, { type: ActionType.TRIBUTE_SUMMON }>,
): number {
  if (action.tributeUids.length < 2) return 0;
  const level = plannerDifficulty(state, seat);
  if (level === 'easy') return 0;
  const summoned = state.zones[seat].hand.find((c) => c.uid === action.cardUid);
  if (!summoned) return 0;
  const reverse = state.reverseStatMods;
  let oneBest = 0;
  let found = false;
  for (const card of state.zones[seat].hand) {
    if (card.uid === summoned.uid) continue;
    const data = getCardData(library, card.cardId);
    if (getCardKind(data) !== CardKind.MONSTER || isExtraDeckCard(data)) continue;
    const lv = getLevel(card, library);
    if (lv < 5 || lv > 6) continue;
    found = true;
    oneBest = Math.max(oneBest, getModifiedAtk(card, library, reverse));
  }
  if (!found) return 0;
  const faceUp = getMonsters(state.zones[otherSeat(seat)]).filter((m) => m.faceUp);
  const barrier = (monster: CardInstance): number =>
    monster.position === CardPosition.ATTACK
      ? getModifiedAtk(monster, library, reverse)
      : getModifiedDef(monster, library, reverse);
  if (!faceUp.every((monster) => oneBest > barrier(monster))) return 0;
  const twoAtk = getModifiedAtk(summoned, library, reverse);
  if (faceUp.some((monster) => twoAtk > barrier(monster) && oneBest <= barrier(monster))) {
    return 0;
  }
  return level === 'normal' ? -800 : -1200;
}

/**
 * The search draws at random, so this bias is the decision. Never mill an
 * Exodia piece or a saved power spell. A bigger opposing hand is the payoff.
 */
function cardDestructionBias(state: MatchState, seat: Seat): number {
  const level = plannerDifficulty(state, seat);
  const hand = state.zones[seat].hand;
  let exodia = 0;
  let saved = 0;
  for (const card of hand) {
    if (EXODIA_PIECE_SET.has(card.cardId)) exodia += 1;
    else if (SAVED_SPELLS.has(card.cardId)) saved += 1;
  }
  if (exodia > 0) return -8000 * exodia;
  if (saved > 0 && level !== 'easy') return -3000 * saved;
  const ours = hand.length;
  const theirs = state.zones[otherSeat(seat)].hand.length;
  if (theirs <= ours) {
    if (aiIsHighDifficulty(level)) return -800;
    if (level === 'normal') return -300;
    return 0;
  }
  const swing = (theirs - ours) * 120;
  if (aiIsHighDifficulty(level)) return 350 + swing;
  if (level === 'normal') return 160 + Math.floor(swing / 2);
  return 0;
}

function equipBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  action: PlayerAction,
  atkBoost: number,
): number {
  const uid = firstTargetUid(action);
  const id = actionCardId(state, seat, action);
  if (id === '50152549') {
    const oppHost = getMonsters(state.zones[otherSeat(seat)]).find((m) => m.uid === uid);
    if (!oppHost?.faceUp) return -80;
    return oppHost.position === CardPosition.ATTACK ? 220 : 40;
  }
  const host = getMonsters(state.zones[seat]).find((m) => m.uid === uid);
  if (!host?.faceUp) return -80;
  const reverse = state.reverseStatMods;
  const atk = getModifiedAtk(host, library, reverse);
  const boosted = atk + (reverse ? -atkBoost : atkBoost);
  const oppFaceUp = getMonsters(state.zones[otherSeat(seat)]).filter((m) => m.faceUp);
  const canNow = oppFaceUp.some((m) => {
    const bar = m.position === CardPosition.ATTACK
      ? getModifiedAtk(m, library, reverse)
      : getModifiedDef(m, library, reverse);
    return atk > bar;
  });
  const canBoosted = oppFaceUp.some((m) => {
    const bar = m.position === CardPosition.ATTACK
      ? getModifiedAtk(m, library, reverse)
      : getModifiedDef(m, library, reverse);
    return boosted > bar;
  });
  if (canBoosted && !canNow) return 240;
  if (id === '83225447') return -300;
  return 50;
}

function fieldSpellBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  field: FieldSpellConfig,
): number {
  const raceDelta = (m: CardInstance, ours: boolean): number => {
    const race = getCardData(library, m.cardId).race;
    if (!race || !m.faceUp) return 0;
    const sign = ours ? 1 : -1;
    if (field.boostedRaces.includes(race)) return sign * field.atkBoost;
    if (field.penalizedRaces?.includes(race)) return -sign * field.atkBoost;
    return 0;
  };
  let net = 0;
  for (const m of getMonsters(state.zones[seat])) net += raceDelta(m, true);
  for (const m of getMonsters(state.zones[otherSeat(seat)])) net += raceDelta(m, false);
  if (state.reverseStatMods) net = -net;
  if (net > 0) return 110;
  if (net < 0) return -220;
  return 15;
}

const SOUL_EXCHANGE = '68005187';
const HEAL_AMOUNT: Record<string, number> = {
  '38199696': 500,
  '11868825': 600,
  '84257639': 1000,
};
const FLIP_WANTS_MONSTER = new Set(['54652250', '7089711']);
const FLIP_WANTS_TRAP = new Set(['33066139', '46461247']);
const FLIP_WANTS_SPELL = new Set(['9076207']);

function removalHoldBias(
  state: MatchState,
  seat: Seat,
  oppPublic: number,
  ourPublic: number,
  ourSwing: ReturnType<typeof forecastCombat>,
  theirSwing: ReturnType<typeof forecastCombat>,
): number {
  const opp = otherSeat(seat);
  const faceUp = getMonsters(state.zones[opp]).filter((m) => m.faceUp);
  if (faceUp.length === 0) return -600;
  if (theirSwing.lethal) return 400;
  if (oppPublic > ourPublic) return 120 + oppPublic * 0.35;
  const oppLp = state.zones[opp].lifePoints;
  if (ourSwing.attackerDamage < oppLp && ourPublic >= oppLp) return 350;
  if (ourSwing.lethal) return -800;
  return -500;
}

function rebornTimingBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): number {
  if (state.phase !== EnginePhase.MAIN_1) return 50;
  if (state.zones[seat].skipBattlePhase) return 50;
  const bestGy = bestGraveyardAtk(state, library);
  const killable = bestKillableAtk(state, library, seat);
  if (killable > bestGy) return -450;
  return 50;
}

function bestGraveyardAtk(state: MatchState, library: CardLibrary): number {
  let best = 0;
  for (const seat of [0, 1] as Seat[]) {
    for (const card of state.zones[seat].graveyard) {
      const data = getCardData(library, card.cardId);
      if (getCardKind(data) !== CardKind.MONSTER) continue;
      best = Math.max(best, getModifiedAtk(card, library, state.reverseStatMods));
    }
  }
  return best;
}

function bestKillableAtk(state: MatchState, library: CardLibrary, seat: Seat): number {
  const opp = getMonsters(state.zones[otherSeat(seat)]).filter((m) => m.faceUp);
  const attackers = getMonsters(state.zones[seat]).filter(
    (m) => m.faceUp && m.position === CardPosition.ATTACK && !m.attackedThisTurn,
  );
  let best = 0;
  for (const target of opp) {
    const bar = target.position === CardPosition.ATTACK
      ? getModifiedAtk(target, library, state.reverseStatMods)
      : getModifiedDef(target, library, state.reverseStatMods);
    const can = attackers.some(
      (a) => getModifiedAtk(a, library, state.reverseStatMods) > bar,
    );
    if (!can) continue;
    best = Math.max(best, getModifiedAtk(target, library, state.reverseStatMods));
  }
  return best;
}

function fusionHandPenalty(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  action: PlayerAction,
): number {
  const fusionUid = firstTargetUid(action);
  if (!fusionUid) return 0;
  const fusion = state.zones[seat].extraDeck.find((c) => c.uid === fusionUid);
  if (!fusion) return 0;
  const names = new Set(
    (getCardData(library, fusion.cardId).desc ?? '').match(/"([^"]+)"/g)?.map((raw) => raw.replace(/"/g, '')) ?? [],
  );
  let penalty = 0;
  for (const card of state.zones[seat].hand) {
    if (card.cardId === AI_CARD.POLYMERIZATION) continue;
    const data = getCardData(library, card.cardId);
    if (!names.has(data.name)) continue;
    if (FLIP_WANTS_MONSTER.has(card.cardId) || FLIP_WANTS_TRAP.has(card.cardId) || FLIP_WANTS_SPELL.has(card.cardId)) {
      penalty += 500;
    } else if (getLevel(card, library) >= 5) {
      penalty += 400 + getModifiedAtk(card, library, state.reverseStatMods) * 0.2;
    } else if (SAVED_SPELLS.has(card.cardId)) {
      penalty += 700;
    }
  }
  return penalty;
}

/** Field materials the fusion will send away. Subtracted on top of the board score. */
export function fusionFieldPenalty(
  before: MatchState,
  after: MatchState,
  library: CardLibrary,
  seat: Seat,
): number {
  const stillOut = new Set(getMonsters(after.zones[seat]).map((m) => m.uid));
  let penalty = 0;
  for (const monster of getMonsters(before.zones[seat])) {
    if (stillOut.has(monster.uid)) continue;
    penalty += getModifiedAtk(monster, library, before.reverseStatMods);
  }
  return penalty;
}

function deSpellBias(state: MatchState, seat: Seat, action: PlayerAction): number {
  const uid = firstTargetUid(action);
  if (!uid) return -120;
  const ours = getSpellTraps(state.zones[seat]).some((c) => c.uid === uid);
  if (ours || state.zones[seat].fieldSpell?.uid === uid) return -500;
  return 90;
}

function soulExchangeBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  action: PlayerAction,
): number {
  const uid = firstTargetUid(action);
  const hand = state.zones[seat].hand;
  const tribute = hand.some((card) => {
    const data = getCardData(library, card.cardId);
    return getCardKind(data) === CardKind.MONSTER && getLevel(card, library) >= 5;
  });
  if (!tribute) return -700;
  const target = uid
    ? getMonsters(state.zones[otherSeat(seat)]).find((m) => m.uid === uid)
    : undefined;
  if (!target) return -200;
  const marks = soulExchangeTributeUids(state, seat);
  if (marks.includes(target.uid) || marks.length >= 2) return -400;
  const atk = target.faceUp ? getModifiedAtk(target, library, state.reverseStatMods) : 400;
  const needsTwo = hand.some((card) => {
    const data = getCardData(library, card.cardId);
    return getCardKind(data) === CardKind.MONSTER && getLevel(card, library) >= 7;
  });
  const stack = marks.length === 1 && needsTwo ? 80 : 0;
  return 80 + stack + Math.min(400, atk * 0.15);
}

function healBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  heal: number,
): number {
  const level = plannerDifficulty(state, seat);
  const lp = state.zones[seat].lifePoints;
  const their = forecastCombat(state, library, otherSeat(seat));
  const floor = level === 'normal' ? 3000 : level === 'easy' ? 8000 : 2000;
  const cancels = -Math.round(heal * LP_W) - 80;
  if (level === 'easy') return 40;
  if (their.lethal && lp + heal > their.attackerDamage) return 400;
  if (lp <= floor) return 60;
  return cancels;
}

function flipSetBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  cardUid: string,
): number {
  const card = state.zones[seat].hand.find((c) => c.uid === cardUid);
  if (!card) return 0;
  const ourSwing = forecastCombat(state, library, seat);
  if (ourSwing.lethal) return -200;
  const opp = state.zones[otherSeat(seat)];
  const hasMonster = getMonsters(opp).some((m) => m.faceUp);
  const hasBackrow = getSpellTraps(opp).some((c) => !c.faceUp) || !!opp.fieldSpell;
  if (FLIP_WANTS_MONSTER.has(card.cardId)) {
    if (hasMonster) return 750;
    // Swords of Revealing Light flips Man-Eater Bug with nothing to destroy.
    if (
      card.cardId === '54652250' &&
      getMonsters(opp).length === 0 &&
      aiIsHighDifficulty(plannerDifficulty(state, seat))
    ) {
      return -500;
    }
    return 160;
  }
  if (FLIP_WANTS_TRAP.has(card.cardId) || FLIP_WANTS_SPELL.has(card.cardId)) {
    return hasBackrow ? 700 : 140;
  }
  if (card.cardId === '31560081' && faithRecyclesStaple(state, library, seat)) {
    return 950;
  }
  const level = plannerDifficulty(state, seat);
  if (level !== 'easy' && level !== 'test' && card.cardId === '51371017') {
    const n = getSpellTraps(opp).length + (opp.fieldSpell ? 1 : 0);
    if (n === 0) return -300;
    // Two backrow have to beat summoning the 900 face-up. 200 each is not enough.
    return n * 200 + (n >= 2 ? 700 : 0);
  }
  if (level !== 'easy' && level !== 'test' && card.cardId === '51275027') {
    const attacker = getMonsters(opp).some(
      (monster) => monster.faceUp && monster.position === CardPosition.ATTACK,
    );
    if (attacker) return 500;
  }
  return 0;
}

/** With three Forbidden One pieces, setting and passing beats opening the board. */
export function exodiaStallBias(
  state: MatchState,
  seat: Seat,
  action: PlayerAction,
): number {
  if (uniqueExodiaPieces(state.zones[seat].hand) < 3) return 0;
  if (action.type === ActionType.SET_MONSTER || action.type === ActionType.SET_SPELL_TRAP) {
    return 280;
  }
  if (action.type === ActionType.ADVANCE_PHASE) return 160;
  if (
    action.type === ActionType.NORMAL_SUMMON ||
    action.type === ActionType.TRIBUTE_SUMMON ||
    action.type === ActionType.FLIP_SUMMON
  ) {
    return -420;
  }
  return 0;
}

/** Spells worth a Magician of Faith set. Unranked graveyard spells do not qualify. */
const FAITH_SPELL_RANK: Record<string, number> = {
  '55144522': 100,
  '12580477': 95,
  '53129443': 90,
  '83764718': 85,
  '4031928': 80,
  '19613556': 75,
  '44095762': 70,
  '41420027': 65,
  '77414722': 60,
  '3819470': 55,
  '4206964': 50,
};

const MIRROR_FORCE = '44095762';

function knownBackrowCount(state: MatchState, viewer: Seat, owner: Seat): number {
  let count = 0;
  for (const card of getSpellTraps(state.zones[owner])) {
    if (card.faceUp || knownCardId(state, viewer, card.uid)) count += 1;
  }
  if (state.zones[owner].fieldSpell) count += 1;
  return count;
}

function knownMirrorForce(state: MatchState, seat: Seat): boolean {
  const opp = otherSeat(seat);
  return getSpellTraps(state.zones[opp]).some((card) => {
    const id = card.faceUp ? card.cardId : knownCardId(state, seat, card.uid);
    return id === MIRROR_FORCE;
  });
}

/** Their face-up Swords is the only thing between this swing and lethal. */
function stormUnlocksLethal(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): boolean {
  if (state.zones[seat].attackLockTurns <= 0) return false;
  const opp = otherSeat(seat);
  const swords = getSpellTraps(state.zones[opp]).some(
    (card) => card.faceUp && card.cardId === AI_CARD.SWORDS,
  );
  if (!swords) return false;
  return forecastCombat(state, library, seat).lethal;
}

/** The one Heavy Storm: two known cards, a seen Mirror Force, or a lock on lethal. */
export function heavyStormWorthIt(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): boolean {
  const opp = otherSeat(seat);
  const theirs = knownBackrowCount(state, seat, opp);
  const ours = knownBackrowCount(state, seat, seat);
  if (theirs >= 2 && theirs > ours) return true;
  if (knownMirrorForce(state, seat) && ours <= 1) return true;
  return stormUnlocksLethal(state, library, seat);
}

function heavyStormBias(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): number {
  if (!heavyStormWorthIt(state, library, seat)) return -800;
  if (knownMirrorForce(state, seat) || stormUnlocksLethal(state, library, seat)) {
    return 720;
  }
  return 480;
}

function faithRecyclesStaple(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): boolean {
  const target = flipTargetUid(state, library, seat, '31560081');
  if (!target) return false;
  const card = state.zones[seat].graveyard.find((grave) => grave.uid === target);
  return !!card && FAITH_SPELL_RANK[card.cardId] !== undefined;
}

/**
 * Hane-Hane returns Magician of Faith or Mask of Darkness when the Graveyard
 * still has a staple that card can set again. Otherwise the highest ATK.
 */
function haneHaneReplayUid(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): string | undefined {
  const level = plannerDifficulty(state, seat);
  if (level === 'easy' || level === 'test') return undefined;
  const bestRank = (kind: CardKind): number => {
    let rank = -1;
    for (const card of state.zones[seat].graveyard) {
      if (getCardKind(getCardData(library, card.cardId)) !== kind) continue;
      const value = FAITH_SPELL_RANK[card.cardId];
      if (value === undefined || value <= rank) continue;
      rank = value;
    }
    return rank;
  };
  const spellRank = bestRank(CardKind.SPELL);
  const trapRank = bestRank(CardKind.TRAP);
  let bestUid: string | undefined;
  let best = -1;
  for (const monster of getMonsters(state.zones[seat])) {
    if (!monster.faceUp) continue;
    const rank = monster.cardId === '31560081'
      ? spellRank
      : monster.cardId === '28933734'
        ? trapRank
        : -1;
    if (rank > best) {
      best = rank;
      bestUid = monster.uid;
    }
  }
  return best >= 0 ? bestUid : undefined;
}

/** Best public Flip target, or a Set whose id this seat has already seen. */
export function flipTargetUid(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  cardId: string,
): string | undefined {
  if (cardId === '31560081' || cardId === '28933734') {
    const want = cardId === '31560081' ? CardKind.SPELL : CardKind.TRAP;
    const gy = state.zones[seat].graveyard.filter(
      (card) => getCardKind(getCardData(library, card.cardId)) === want,
    );
    if (gy.length === 0) return undefined;
    return gy.reduce((best, card) =>
      (FAITH_SPELL_RANK[card.cardId] ?? 10) > (FAITH_SPELL_RANK[best.cardId] ?? 10)
        ? card
        : best,
    ).uid;
  }
  const opp = otherSeat(seat);
  if (cardId === '7089711') {
    const replay = haneHaneReplayUid(state, library, seat);
    if (replay) return replay;
  }
  if (FLIP_WANTS_MONSTER.has(cardId)) {
    const faceUp = getMonsters(state.zones[opp]).filter((m) => m.faceUp);
    if (faceUp.length === 0) return undefined;
    return faceUp.reduce((best, m) =>
      getModifiedAtk(m, library, state.reverseStatMods) >
      getModifiedAtk(best, library, state.reverseStatMods)
        ? m
        : best,
    ).uid;
  }
  const wantTrap = FLIP_WANTS_TRAP.has(cardId);
  const wantSpell = FLIP_WANTS_SPELL.has(cardId);
  if (!wantTrap && !wantSpell) return undefined;
  const own = getSpellTraps(state.zones[seat]);
  const ownUids = new Set(own.map((card) => card.uid));
  const rows = [...getSpellTraps(state.zones[opp]), ...own];
  const known = rows.find((card) => {
    // Own Sets are not hidden from this seat. Opponent Sets stay blind
    // unless a peek already wrote them into aiKnownCards.
    const id =
      card.faceUp || ownUids.has(card.uid)
        ? card.cardId
        : knownCardId(state, seat, card.uid);
    if (!id) return false;
    const kind = getCardKind(getCardData(library, id));
    return wantTrap ? kind === CardKind.TRAP : kind === CardKind.SPELL;
  });
  if (known) return known.uid;
  // Mandatory Flip: a face-down Set is a legal blind target. Leaving this
  // empty used to skip the pick and freeze the vs-AI host.
  return rows.find((card) => !card.faceUp)?.uid ?? rows[0]?.uid;
}

/**
 * Spells whose activation should be dropped when combat outlook does not improve.
 * Pot of Greed is excluded (drawing does not change the board).
 */
export function isCombatSensitiveSpell(cardId: string): boolean {
  if (
    cardId === AI_CARD.FISSURE ||
    cardId === AI_CARD.MONSTER_REBORN ||
    cardId === AI_CARD.CHANGE_OF_HEART ||
    cardId === AI_CARD.POLYMERIZATION ||
    cardId === AI_CARD.STOP_DEFENSE ||
    cardId === AI_CARD.DARK_HOLE ||
    cardId === '19613556'
  ) {
    return true;
  }
  if (getEquipConfig(Number(cardId))) return true;
  if (getFieldSpellConfig(Number(cardId))) return true;
  return false;
}

export { actionCardId };

/** Highest-ATK extra-deck Fusion this seat can name as a Polymerization target. */
export function extraFusionsByAtk(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): CardInstance[] {
  return [...state.zones[seat].extraDeck]
    .filter((c) => isExtraDeckCard(getCardData(library, c.cardId)))
    .sort(
      (a, b) =>
        getModifiedAtk(b, library, state.reverseStatMods) -
        getModifiedAtk(a, library, state.reverseStatMods),
    );
}
